import triggerTracking, {
    PageShowEvent
} from '@blueprint/tracking/dist/Tracking';
import snakeCase from 'lodash/snakeCase';

import { getApolloClient } from '../graphql/client';

import { USER_QUERY } from 'queries/user/user.query';
import { FetchUserQuery } from 'queries/user/user.query.generated';
import AB_TEST_QUERY from 'queries/abTest/abTest.graphql';
import { GetAbTestsQuery } from 'queries/abTest/abTest.generated';

import { trackPage as trackSegmentPage } from './segment/trackPage';
import { ANALYTICS_BASE_TAXONOMY } from 'shared/tracking/constants';
import { Taxonomy } from 'shared/tracking/contracts';
import { logError } from 'shared/logger';

type DynamicTaxonomy = Record<string, unknown>;

const removePropertiesWithNullValues = <T extends DynamicTaxonomy>(obj: T) =>
    Object.entries(obj).reduce<T>(
        (acc, [key, value]) => ({
            ...acc,
            ...(value !== null && value !== undefined && { [key]: value })
        }),
        {} as T
    );

export const snakeCaseKeys = <T extends DynamicTaxonomy>(obj: T) =>
    Object.entries(obj).reduce<T>(
        (acc, [key, value]) => ({
            ...acc,
            ...{ [snakeCase(key)]: value }
        }),
        {} as T
    );

type ABTests = Record<string, string>;

export const getABTests = async (): Promise<ABTests | null> => {
    const {
        data: { abTests },
        errors
    } = await getApolloClient().query<GetAbTestsQuery>({
        query: AB_TEST_QUERY
    });
    if (errors) {
        logError(errors);
    }

    return (
        abTests?.reduce((acc, { id, name }) => ({ ...acc, [id]: name }), {}) ??
        null
    );
};

export const getUserTaxonomy = async () => {
    const {
        data: { user },
        errors
    } = await getApolloClient().query<FetchUserQuery>({
        // TODO: replace with useIsLoggedIn hook for user state
        query: USER_QUERY
    });

    if (errors) {
        logError(errors);
    }

    return {
        signed_in_status: user?.loggedIn ? 'signed in' : 'signed out',
        about_me_status: user?.senderPropertyStatus,
        registration_platform: user?.registrationSources?.join(', '),
        user_creation_date: user?.creationDate,
        user_id: user?.userId,
        user_uuid: user?.userUuid,
        registered: user?.registered
    };
};

export const getAnalyticsEventData = async (
    page: string,
    extraTaxonomy: Record<string, unknown> = {},
    extraEcommerce: Record<string, unknown> | null = null
) => {
    const baseTaxonomy = {
        ...ANALYTICS_BASE_TAXONOMY,
        page
    };

    const [userTaxonomy, abTests] = await Promise.all([
        getUserTaxonomy(),
        getABTests()
    ]);

    const taxonomy = snakeCaseKeys(
        removePropertiesWithNullValues({
            ...baseTaxonomy,
            ...userTaxonomy,
            ...extraTaxonomy
        })
    );

    const ecommerce = snakeCaseKeys(
        removePropertiesWithNullValues({
            ...extraEcommerce
        })
    );

    return {
        taxonomy,
        ...(extraEcommerce && { ecommerce }),
        ...(abTests && { ab: abTests })
    };
};

export const trackPage = async (
    page: string,
    extraTaxonomy: Record<string, unknown> = {},
    extraEcommerce: Record<string, unknown> | null = null
) => {
    const analyticsEventData = await getAnalyticsEventData(
        page,
        extraTaxonomy,
        extraEcommerce
    );
    const pageShowEvent: PageShowEvent & { ab?: ABTests } = {
        event: 'pageshow',
        ...analyticsEventData
    };

    triggerTracking(pageShowEvent);

    // unknown as a temporary way to reuse current taxonomy definition
    trackSegmentPage((analyticsEventData.taxonomy as unknown) as Taxonomy);
};
